import {
  useEffect,
  useRef,
  useContext,
  useState,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react'
import './ConfigureForm.scss'
import { DatasetContext } from '../../context'
import { Button, RadioGroup, Select, Slider } from 'component-library'
import { gql, useLazyQuery } from '@apollo/client'
import { formatDecimal, countDecimals } from '../../utils/number_actions'
import { PERIODICITY_RADIO_BUTTONS } from './helpers'
import { MultiValue, SingleValue } from 'react-select'

interface IConfigureFormProps {
  category: string
  name: string
  isVisible: boolean
}

interface IOption {
  value: string
  label: string
}

export const GET_UPDATED_PARAMETER_OPTIONS = gql(`
  query GetUpdatedParameterOptions($slug: String!, $selection: JSON) {
    datasetFamily(slug: $slug) {
      parameterOptions(currentSelection: $selection) {
        options
        selection
      }
    }
  }`)

const ConfigureForm = (props: IConfigureFormProps) => {
  const { category, name, isVisible } = props
  const ref = useRef<HTMLDivElement>(null)
  const {
    parameterDefaults,
    setSelectedBondlength,
    selectedBondlength,
    selectedBondAngle,
    setSelectedBondAngle,
    selectedBasis,
    setSelectedBasis,
    selectedPeriodicity,
    selectedNumSpinOrbitals,
    setSelectedNumSpinOrbitals,
    setSelectedPeriodicity,
    selectedLayout,
    setSelectedLayout,
    setSelectedLattice,
    selectedLattice,
    pageSlug,
    parameterOptions,
    setParameterOptions,
  } = useContext(DatasetContext)

  const [localBondLength, setLocalBondLength] = useState(selectedBondlength)
  const setLocalBondAngle = useState(selectedBondAngle)[1]

  const [getUpdatedParameterOptions, { loading, error, data }] = useLazyQuery(
    GET_UPDATED_PARAMETER_OPTIONS,
    {
      fetchPolicy: 'no-cache',
    }
  )

  useEffect(() => {
    if (isVisible && ref.current) {
      const bounding = ref.current.getBoundingClientRect()
      if (
        bounding.bottom >
        (window.innerHeight || document.documentElement.clientHeight)
      ) {
        ref.current.style.top = `${
          ((window.innerHeight || document.documentElement.clientHeight) -
            bounding.bottom) /
          4
        }px`
      }
      setLocalBondLength(selectedBondlength)
    }
  }, [isVisible])

  useEffect(() => {
    let selection = {}
    if (category === 'qchem') {
      selection = {
        bondlength: selectedBondlength,
        bondangle: selectedBondAngle,
        number_of_spin_orbitals: selectedNumSpinOrbitals,
        basis: selectedBasis,
      }
    } else if (category == 'qspin') {
      selection = {
        periodicity: selectedPeriodicity,
        lattice: selectedLattice,
        layout: selectedLayout,
      }
    }

    getUpdatedParameterOptions({
      variables: {
        slug: pageSlug,
        selection,
      },
    })
  }, [
    selectedBondlength,
    selectedBondAngle,
    selectedBasis,
    selectedLayout,
    selectedPeriodicity,
    selectedLattice,
    selectedNumSpinOrbitals,
  ])

  useEffect(() => {
    if (
      !error &&
      !loading &&
      typeof data !== 'undefined' &&
      data.datasetFamily
    ) {
      setParameterOptions(data.datasetFamily.parameterOptions)
    }
    if (error) {
      console.error(error)
    }
  }, [data, error, loading])

  const bondLengthOptions = useMemo(
    () =>
      parameterOptions.options['bondlength']
        ? [...parameterOptions.options['bondlength']]
            .map((option) => formatDecimal(Number(option)))
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        : [],
    [parameterOptions]
  )

  const bondAngleOptions = useMemo(
    () =>
      parameterOptions.options['bondangle']
        ? [...parameterOptions.options['bondangle']]
        : [],
    [parameterOptions]
  )

  const basisOptions = useMemo(
    () =>
      parameterOptions.options['basis']
        ? [...parameterOptions.options['basis']]
        : [],
    [parameterOptions]
  )
  const numSpinOrbitalsOptions = useMemo(
    () =>
      parameterOptions.options['number_of_spin_orbitals']
        ? [...parameterOptions.options['number_of_spin_orbitals']]
        : [],
    [parameterOptions]
  )

  const layoutOptions = useMemo(
    () =>
      parameterOptions.options['layout']
        ? [...parameterOptions.options['layout'], 'all']
        : [],
    [parameterOptions]
  )

  const latticeOptions = useMemo(
    () =>
      parameterOptions.options['lattice']
        ? [...parameterOptions.options['lattice'], 'all']
        : [],
    [parameterOptions]
  )
  useEffect(() => {
    if (!bondLengthOptions.includes(formatDecimal(selectedBondlength))) {
      setSelectedBondlength(Number(bondLengthOptions[0]))
    }
    if (!bondAngleOptions.includes(selectedBondAngle)) {
      setSelectedBondAngle(bondAngleOptions[0])
    }
    if (!basisOptions.includes(selectedBasis)) {
      setSelectedBasis(basisOptions[0])
    }
    if (!numSpinOrbitalsOptions.includes(selectedNumSpinOrbitals)) {
      setSelectedNumSpinOrbitals(numSpinOrbitalsOptions[0])
    }
    if (!layoutOptions.includes(selectedLayout)) {
      setSelectedLayout(layoutOptions[0])
    }
    if (!latticeOptions.includes(selectedLattice)) {
      setSelectedLattice(latticeOptions[0])
    }
  }, [
    selectedBondlength,
    bondLengthOptions,
    selectedBondAngle,
    bondLengthOptions,
    selectedBasis,
    basisOptions,
    selectedLayout,
    layoutOptions,
    selectedLattice,
    latticeOptions,
  ])

  const resetDefaultParameters = () => {
    setSelectedBondlength(Number(parameterDefaults.selection['bondlength']))
    setLocalBondLength(Number(parameterDefaults.selection['bondlength']))
    setSelectedBondAngle(parameterDefaults.selection['bondangle'])
    setLocalBondAngle(parameterDefaults.selection['bondangle'])
    setSelectedBasis(parameterDefaults.selection['basis'])
    setSelectedLayout(parameterDefaults.selection['layout'])
    setSelectedPeriodicity(PERIODICITY_RADIO_BUTTONS[0].value)
    setSelectedLattice(parameterDefaults.selection['lattice'])
    setSelectedNumSpinOrbitals(parameterDefaults.selection['num_spin_orbitals'])
  }

  const updateBasis = (
    newValue:
      | MultiValue<{ value: string; label: string }>
      | SingleValue<{ value: string; label: string }>
  ) => {
    const newValueLocal = newValue as IOption
    setSelectedBasis(newValueLocal.value)
  }

  const updateLayout = (
    newValue:
      | MultiValue<{ value: string; label: string }>
      | SingleValue<{ value: string; label: string }>
  ) => {
    const newValueLocal = newValue as IOption
    setSelectedLayout(newValueLocal.value)
  }

  const updateLattice = (
    newValue:
      | MultiValue<{ value: string; label: string }>
      | SingleValue<{ value: string; label: string }>
  ) => {
    const newValueLocal = newValue as IOption
    setSelectedLattice(newValueLocal.value)
  }

  const hcnMoleculeBondLength = (
    <div className="flex justify-between">
      <div>
        <span>Bond length:</span>
      </div>
      <div>
        {loading ? (
          <span className="italic text-grey-5">Loading...</span>
        ) : (
          <span>{`${bondAngleOptions[0]} Å`}</span>
        )}
      </div>
    </div>
  )

  const hcnMoleculeBondAngle = (
    <Slider
      step={0.001}
      testId="bond-angle-slider"
      isLoading={loading}
      label="Bond angle"
      labelId="bond-angle-slider"
      minValue={0}
      maxValue={bondLengthOptions.length - 1}
      formatMaxValue={() => String(localBondLength)}
      value={bondLengthOptions.findIndex(
        (element) => element === String(selectedBondlength)
      )}
      defaultValue={
        Number(
          bondLengthOptions.findIndex(
            (element) => element === parameterDefaults.selection['bondlength']
          )
        ) || 0
      }
      onChange={(newValue) => {
        setLocalBondLength(Number(bondLengthOptions[Math.floor(newValue)]))
      }}
      onAfterChange={(newValue) => {
        setSelectedBondlength(Number(bondLengthOptions[Math.floor(newValue)]))
      }}
      hideMinValue
    />
  )

  return isVisible ? (
    <>
      {category === 'qchem' ? (
        <div
          className="ConfigureForm"
          ref={ref}
          data-testid="qchem-configure-form"
        >
          <div className="p-6 border border-x-transparent border-t-transparent border-b-grey-5 text-sm">
            {name === 'HCN Molecule' ? (
              hcnMoleculeBondLength
            ) : bondLengthOptions.length > 1 ? (
              <Slider
                step={0.001}
                testId="bond-length-slider"
                isLoading={loading}
                label="Bond length"
                labelId="bond-length-slider"
                minValue={0}
                maxValue={bondLengthOptions.length - 1}
                formatMaxValue={() =>
                  bondLengthOptions.includes(formatDecimal(localBondLength))
                    ? `${localBondLength.toFixed(
                        countDecimals(localBondLength, 1)
                      )} Å`
                    : `${Number(bondLengthOptions[0]).toFixed(
                        countDecimals(localBondLength, 1)
                      )} Å`
                }
                value={
                  bondLengthOptions.includes(formatDecimal(selectedBondlength))
                    ? bondLengthOptions.findIndex(
                        (element) =>
                          element === formatDecimal(selectedBondlength)
                      )
                    : Number(bondLengthOptions[0]) || 0
                }
                defaultValue={
                  Number(
                    bondLengthOptions.findIndex(
                      (element) =>
                        element === parameterDefaults.selection['bondlength']
                    )
                  ) || 0
                }
                onChange={(newValue) => {
                  setLocalBondLength(
                    Number(bondLengthOptions[Math.floor(newValue)])
                  )
                }}
                onAfterChange={(newValue) => {
                  setSelectedBondlength(
                    Number(bondLengthOptions[Math.floor(newValue)])
                  )
                }}
                hideMinValue
              />
            ) : (
              <div className="flex justify-between">
                <div>
                  <span>Bond length:</span>
                </div>
                <div>
                  {loading ? (
                    <span className="italic text-grey-5">Loading...</span>
                  ) : (
                    <span>
                      {bondLengthOptions.includes(
                        formatDecimal(selectedBondlength)
                      )
                        ? `${formatDecimal(
                            selectedBondlength,
                            countDecimals(selectedBondlength, 1)
                          )} Å`
                        : `${bondLengthOptions[0]} Å`}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="mt-3 mb-4 text-sm">
              {name === 'HCN Molecule' ? (
                hcnMoleculeBondAngle
              ) : (
                <div className="flex justify-between">
                  <div>
                    <span>Bond angle:</span>
                  </div>
                  <div>
                    {loading ? (
                      <span className="italic text-grey-5">Loading...</span>
                    ) : (
                      <span>
                        {bondAngleOptions.includes(selectedBondAngle)
                          ? selectedBondAngle
                          : bondAngleOptions[0]}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-between text-sm mb-2">
              <div>
                <span>Number of spin orbitals:</span>
              </div>
              <div>
                {loading ? (
                  <span className="italic text-grey-5">Loading...</span>
                ) : (
                  <span>
                    {parameterOptions.selection['number_of_spin_orbitals']}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center px-6 py-6 border border-x-transparent border-t-transparent border-b-grey-5 text-sm">
            <div>
              <span>Basis:</span>
            </div>
            <div>
              <Select
                testId="basis-select"
                isLoading={loading}
                loadingMessage={() => 'Loading...'}
                formName="configureForm"
                fieldName="basis"
                fieldLabel="basis"
                fieldLabelHidden
                defaultValue={{
                  value: parameterDefaults.selection['basis'],
                  label: parameterDefaults.selection['basis'],
                }}
                options={basisOptions.map((option) => {
                  return { value: option, label: option }
                })}
                value={
                  basisOptions.includes(selectedBasis)
                    ? { value: selectedBasis, label: selectedBasis }
                    : { value: basisOptions[0], label: basisOptions[0] }
                }
                onChange={(newValue) => updateBasis(newValue)}
              />
            </div>
          </div>
          <div className="w-full flex justify-center py-3">
            <Button
              type="text"
              label="Reset default parameters"
              leftIcon={<i className="bx bx-refresh"></i>}
              className="ConfigureForm__reset-button"
              variation="light-blue"
              onClick={resetDefaultParameters}
              data-testid="reset-parameters-button"
            />
          </div>
        </div>
      ) : (
        <div
          className="ConfigureForm"
          ref={ref}
          data-testid="qspin-configure-form"
        >
          <div className="p-6 border border-x-transparent border-t-transparent border-b-grey-5 text-sm">
            <div className="flex justify-between items-center">
              <div>
                <span>Layout:</span>
              </div>
              <div>
                <Select
                  testId="layout-select"
                  isLoading={loading}
                  loadingMessage={() => 'Loading...'}
                  formName="configureForm"
                  fieldName="layout"
                  fieldLabel="Layout"
                  fieldLabelHidden
                  defaultValue={{
                    value: parameterDefaults.selection['layout'],
                    label: parameterDefaults.selection['layout'],
                  }}
                  options={layoutOptions.map((option) => {
                    return { value: option, label: option }
                  })}
                  value={
                    layoutOptions.includes(selectedLayout)
                      ? { value: selectedLayout, label: selectedLayout }
                      : { value: layoutOptions[0], label: layoutOptions[0] }
                  }
                  onChange={(newValue) => updateLayout(newValue)}
                />
              </div>
            </div>
            <div className="flex gap-14 my-4">
              <div>
                <span>Periodicity:</span>
              </div>
              <div
                className="ConfigureForm__radio-buttons"
                data-testid="configuration-radio-buttons"
              >
                <RadioGroup
                  radioButtons={PERIODICITY_RADIO_BUTTONS}
                  selectedValue={selectedPeriodicity}
                  onChange={
                    setSelectedPeriodicity as Dispatch<SetStateAction<string>>
                  }
                />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <span>Lattice:</span>
              </div>
              <div>
                <Select
                  testId="lattice-select"
                  isLoading={loading}
                  loadingMessage={() => 'Loading...'}
                  formName="configureForm"
                  fieldName="lattice"
                  fieldLabel="lattice"
                  fieldLabelHidden
                  defaultValue={{
                    value: parameterDefaults.selection['lattice'],
                    label: parameterDefaults.selection['lattice'],
                  }}
                  options={latticeOptions.map((option) => {
                    return { value: option, label: option }
                  })}
                  value={
                    latticeOptions.includes(selectedLattice)
                      ? { value: selectedLattice, label: selectedLattice }
                      : { value: latticeOptions[0], label: latticeOptions[0] }
                  }
                  onChange={(newValue) => updateLattice(newValue)}
                />
              </div>
            </div>
          </div>
          <div className="w-full flex justify-center py-3">
            <Button
              type="text"
              label="Reset default parameters"
              leftIcon={<i className="bx bx-refresh"></i>}
              className="ConfigureForm__reset-button"
              variation="light-blue"
              onClick={resetDefaultParameters}
              data-testid="reset-parameters-button"
            />
          </div>
        </div>
      )}
    </>
  ) : null
}

export default ConfigureForm
